export const maskCEP = (cep: string) => {
  return cep.replace(/^(\d{5})(\d{3})$/, '$1-$2')
}

export const unmaskCEP = (cep: string) => {
  return cep?.replace(/\D/g, '')
}

export const isValidCEPSize = (cep?: string | null) => {
  if (!cep) {
    return false
  }

  const unmaskedCep = unmaskCEP(cep)

  return unmaskedCep?.length === 8 || cep.includes('*')
}

const fallbackHash = (value: string) => {
  let hash = 5381

  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33) ^ value.charCodeAt(i)
  }

  return Math.abs(hash).toString(36)
}

type BufferLike = {
  from: (
    input: string,
    encoding: 'utf-8' | 'latin1'
  ) => { toString: (encoding: 'base64') => string }
}

const isBufferLike = (value: unknown): value is BufferLike => {
  return (
    (typeof value === 'object' || typeof value === 'function') &&
    value !== null &&
    'from' in value &&
    typeof (value as { from?: unknown }).from === 'function'
  )
}

const toBase64 = (value: string, encoding: 'utf-8' | 'latin1' = 'utf-8') => {
  if (typeof globalThis.btoa === 'function') {
    return globalThis.btoa(value)
  }

  const buffer = Reflect.get(globalThis, 'Buffer')

  if (isBufferLike(buffer)) {
    return buffer.from(value, encoding).toString('base64')
  }

  return value
}

export const getCepHashBase64 = async (cep: string) => {
  const normalizedCep = unmaskCEP(cep)

  if (!normalizedCep) {
    return ''
  }

  try {
    if (!globalThis.crypto?.subtle) {
      throw new Error('SubtleCrypto not available')
    }

    const data = new TextEncoder().encode(normalizedCep)
    const digest = await globalThis.crypto.subtle.digest('SHA-256', data)
    const bytes = new Uint8Array(digest)
    const binaryString = Array.from(bytes, (byte) =>
      String.fromCharCode(byte)
    ).join('')

    return toBase64(binaryString, 'latin1')
  } catch {
    return toBase64(fallbackHash(normalizedCep))
  }
}
